import { useState } from 'react';
import { Alert, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import type { LoginResponse } from '@portal/shared';
import { passwordSchema } from '@portal/shared';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { AuthLayout } from './AuthLayout';

const PASSWORD_HINT = 'At least 10 characters with upper-case, lower-case letters and a number.';

function usePasswordFields() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const strength = passwordSchema.safeParse(password);
  const problem = !password ? null : !strength.success ? strength.error.issues[0]?.message : confirm && confirm !== password ? 'Passwords do not match' : null;
  const valid = strength.success && confirm === password;
  const fields = (
    <>
      <TextField label="New password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} helperText={problem ?? PASSWORD_HINT} error={!!problem} fullWidth size="medium" />
      <TextField label="Confirm password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} fullWidth size="medium" />
    </>
  );
  return { password, valid, fields };
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a link to choose a new password.">
      {sent ? (
        <Stack gap={2}>
          <Alert severity="success">If an account exists for {email}, a reset link is on its way. It expires in 1 hour.</Alert>
          <Link component={RouterLink} to="/login">Back to sign in</Link>
        </Stack>
      ) : (
        <form onSubmit={submit}>
          <Stack gap={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus fullWidth size="medium" />
            <Button type="submit" variant="contained" size="large" disabled={!email}>Send reset link</Button>
            <Link component={RouterLink} to="/login" fontSize={14}>Back to sign in</Link>
          </Stack>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { password, valid, fields } = usePasswordFields();
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <AuthLayout title="Choose a new password">
      {done ? (
        <Stack gap={2}>
          <Alert severity="success">Your password has been updated.</Alert>
          <Button component={RouterLink} to="/login" variant="contained">Sign in</Button>
        </Stack>
      ) : (
        <form onSubmit={submit}>
          <Stack gap={2}>
            {!token && <Alert severity="error">This link is missing its token. Request a new one.</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            {fields}
            <Button type="submit" variant="contained" size="large" disabled={!valid || !token}>Update password</Button>
          </Stack>
        </form>
      )}
    </AuthLayout>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { password, valid, fields } = usePasswordFields();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const s = await api.post<LoginResponse>('/auth/accept-invite', { token, password, name: name || undefined });
      setSession(s);
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <AuthLayout title="Welcome!" subtitle="Set up your account to get started.">
      <form onSubmit={submit}>
        <Stack gap={2}>
          {!token && <Alert severity="error">This invitation link is incomplete.</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="medium" />
          {fields}
          <Button type="submit" variant="contained" size="large" disabled={!valid || !token}>Create account</Button>
          <Typography fontSize={13} color="text.secondary">Invitation links expire after 7 days.</Typography>
        </Stack>
      </form>
    </AuthLayout>
  );
}
